import React, { useState, useEffect } from 'react';
import { Settings, Save, Image as ImageIcon, CheckCircle, ShieldCheck, Bell, User as UserIcon, Palette, MapPin, FlaskConical, CalendarCheck, FileText } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { adminApi } from '../../lib/api';
import { AdminNotificationSettings } from './AdminNotificationSettings';
import { AccountTab, ProRdvHoursTab } from '../Settings/SettingsPage';
import { DocumentTemplatesSettings } from '../Settings/DocumentTemplatesSettings';

const DEFAULT_EMAIL_BRAND_COLOR = '#987b55';

const AdminSettings = () => {
    const location = useLocation();
    const initialTab = (location.state as { tab?: 'compte' | 'general' | 'notifications' | 'rdv-pro' | 'documentation' } | null)?.tab;
    const [tab, setTab] = useState<'compte' | 'general' | 'notifications' | 'rdv-pro' | 'documentation'>(initialTab || 'general');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [successMsg, setSuccessMsg] = useState('');
    const [errorMsg, setErrorMsg] = useState('');

    const [loginLogo, setLoginLogo] = useState<string | null>(null);
    const [emailBrandColor, setEmailBrandColor] = useState(DEFAULT_EMAIL_BRAND_COLOR);
    const [openRouteServiceApiKey, setOpenRouteServiceApiKey] = useState('');
    const [testingKey, setTestingKey] = useState(false);
    const [testResult, setTestResult] = useState<{ success: boolean; text: string } | null>(null);

    const handleTestKey = async () => {
        setTestingKey(true);
        setTestResult(null);
        try {
            const res: any = await adminApi.testOpenRouteServiceKey(openRouteServiceApiKey);
            if (res?.success) {
                setTestResult({ success: true, text: `Ça fonctionne — distance calculée Paris → Lyon : ${res.km.toFixed(1)} km.` });
            } else {
                setTestResult({ success: false, text: res?.error || 'Échec du test.' });
            }
        } catch (e: any) {
            setTestResult({ success: false, text: e?.message || 'Échec du test.' });
        } finally {
            setTestingKey(false);
        }
    };

    useEffect(() => {
        loadSettings();
    }, []);

    const loadSettings = async () => {
        try {
            const data = await adminApi.getGlobalSettings();
            if (data.loginLogo) setLoginLogo(data.loginLogo);
            if (data.emailBrandColor) setEmailBrandColor(data.emailBrandColor);
            if (data.openRouteServiceApiKey) setOpenRouteServiceApiKey(data.openRouteServiceApiKey);
        } catch (e: any) {
            console.error('Failed to load global settings', e);
        } finally {
            setLoading(false);
        }
    };

    const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = async (ev) => {
            const base64 = ev.target?.result as string;
            setLoginLogo(base64);
        };
        reader.readAsDataURL(file);
    };

    const saveSettings = async () => {
        setSaving(true);
        setErrorMsg('');
        setSuccessMsg('');
        try {
            await adminApi.saveGlobalSettings({ loginLogo, emailBrandColor, openRouteServiceApiKey });
            setSuccessMsg('Paramètres globaux sauvegardés avec succès.');
            setTimeout(() => setSuccessMsg(''), 3000);
        } catch (e: any) {
            setErrorMsg(e.message || 'Erreur lors de la sauvegarde');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="p-8 text-center text-content-muted">Chargement...</div>;

    return (
        <div className="p-6 md:p-8 max-w-4xl mx-auto space-y-8">
            <div className="flex items-center gap-3 mb-8">
                <div className="w-12 h-12 rounded-2xl bg-surface-soft flex items-center justify-center text-slate-600 dark:text-slate-300">
                    <Settings size={24} />
                </div>
                <div>
                    <h1 className="text-2xl font-black text-content tracking-tight">Paramètres de la plateforme</h1>
                    <p className="text-sm text-content-muted mt-1 font-medium">Configurations globales pour tous les utilisateurs</p>
                </div>
            </div>

            {errorMsg && (
                <div className="p-4 bg-red-500/10 border border-red-500/20 text-red-500 rounded-xl text-sm font-medium">
                    {errorMsg}
                </div>
            )}
            
            {successMsg && (
                <div className="p-4 bg-green-500/10 border border-green-500/20 text-green-500 rounded-xl text-sm font-medium flex items-center gap-2">
                    <CheckCircle size={18} />
                    {successMsg}
                </div>
            )}

            <div className="bg-surface border border-border rounded-2xl p-2 inline-flex gap-2">
                <button onClick={() => setTab('compte')} className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition-all ${tab === 'compte' ? 'bg-brand-600 text-brand-fg' : 'text-content-muted hover:text-content'}`}>
                    <UserIcon size={14} /> Compte
                </button>
                <button onClick={() => setTab('general')} className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition-all ${tab === 'general' ? 'bg-brand-600 text-brand-fg' : 'text-content-muted hover:text-content'}`}>
                    <Settings size={14} /> Général
                </button>
                <button onClick={() => setTab('notifications')} className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition-all ${tab === 'notifications' ? 'bg-brand-600 text-brand-fg' : 'text-content-muted hover:text-content'}`}>
                    <Bell size={14} /> Notifications
                </button>
                <button onClick={() => setTab('rdv-pro')} className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition-all ${tab === 'rdv-pro' ? 'bg-brand-600 text-brand-fg' : 'text-content-muted hover:text-content'}`}>
                    <CalendarCheck size={14} /> RDV Pro
                </button>
                <button onClick={() => setTab('documentation')} className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition-all ${tab === 'documentation' ? 'bg-brand-600 text-brand-fg' : 'text-content-muted hover:text-content'}`}>
                    <FileText size={14} /> Démarches client
                </button>
            </div>

            {tab === 'compte' && <AccountTab />}
            {tab === 'notifications' && <AdminNotificationSettings />}
            {tab === 'rdv-pro' && <ProRdvHoursTab />}
            {tab === 'documentation' && <DocumentTemplatesSettings canOfferAppointment />}

            {tab === 'general' && (
            <>
            <div className="bg-surface border border-border rounded-2xl overflow-hidden shadow-sm">
                <div className="p-6 border-b border-border">
                    <h2 className="text-lg font-bold text-content flex items-center gap-2">
                        <ImageIcon size={20} className="text-brand-500" />
                        Page de Connexion
                    </h2>
                    <p className="text-sm text-content-muted mt-1">Personnalisez le logo affiché sur la page de connexion globale</p>
                </div>
                <div className="p-6 space-y-6">
                    <div>
                        <label className="text-xs font-bold text-content-muted uppercase tracking-widest block mb-4">Logo principal</label>
                        <div className="flex items-start gap-6">
                            <div className="w-32 h-32 rounded-xl bg-surface-soft/50 border-2 border-dashed border-border flex items-center justify-center overflow-hidden shrink-0">
                                {loginLogo ? (
                                    <img src={loginLogo} alt="Logo Login" className="w-full h-full object-contain p-2" />
                                ) : (
                                    <div className="flex flex-col items-center gap-2 text-content-muted">
                                        <ShieldCheck size={32} />
                                        <span className="text-xs font-medium">Par défaut</span>
                                    </div>
                                )}
                            </div>
                            <div className="flex-1 space-y-3">
                                <p className="text-sm text-content-muted">
                                    Ce logo sera visible par tous les utilisateurs (admins, clients, employés) sur la page de login de L'IAmani.
                                </p>
                                <div className="flex items-center gap-3">
                                    <label className="px-4 py-2 bg-surface-soft hover:bg-slate-200 dark:hover:bg-slate-700 text-content text-sm font-bold rounded-lg cursor-pointer transition-colors inline-flex items-center gap-2 border border-border">
                                        <ImageIcon size={16} />
                                        Changer le logo
                                        <input type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
                                    </label>
                                    {loginLogo && (
                                        <button 
                                            onClick={() => setLoginLogo(null)}
                                            className="px-4 py-2 text-red-500 hover:bg-red-500/10 text-sm font-bold rounded-lg transition-colors"
                                        >
                                            Rétablir par défaut
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="bg-surface border border-border rounded-2xl overflow-hidden shadow-sm">
                <div className="p-6 border-b border-border">
                    <h2 className="text-lg font-bold text-content flex items-center gap-2">
                        <Palette size={20} className="text-brand-500" />
                        Emails
                    </h2>
                    <p className="text-sm text-content-muted mt-1">Couleur d'en-tête des emails envoyés depuis votre compte superadmin vers les clients</p>
                </div>
                <div className="p-6">
                    <div className="flex items-center gap-6">
                        <div className="w-32 h-20 rounded-xl overflow-hidden border border-border shrink-0">
                            <div className="w-full h-full flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: emailBrandColor }}>
                                Aperçu
                            </div>
                        </div>
                        <div className="flex-1 space-y-3">
                            <p className="text-sm text-content-muted">Cette couleur s'applique à l'en-tête de tous les emails que vous envoyez (bienvenue, identifiants, réponses au support, invitations, etc.).</p>
                            <div className="flex items-center gap-3">
                                <input type="color" value={emailBrandColor} onChange={(e) => setEmailBrandColor(e.target.value)}
                                    className="w-12 h-10 rounded-lg border border-border cursor-pointer bg-transparent" />
                                <input type="text" value={emailBrandColor} onChange={(e) => setEmailBrandColor(e.target.value)}
                                    className="w-32 px-3 py-2 rounded-lg bg-surface-soft border border-border text-content text-sm font-mono outline-none focus:border-brand-500" />
                                {emailBrandColor !== DEFAULT_EMAIL_BRAND_COLOR && (
                                    <button onClick={() => setEmailBrandColor(DEFAULT_EMAIL_BRAND_COLOR)} className="px-3 py-2 text-red-500 hover:bg-red-500/10 text-sm font-bold rounded-lg transition-colors">
                                        Rétablir par défaut
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="bg-surface border border-border rounded-2xl overflow-hidden shadow-sm">
                <div className="p-6 border-b border-border">
                    <h2 className="text-lg font-bold text-content flex items-center gap-2">
                        <MapPin size={20} className="text-brand-500" />
                        Frais kilométriques
                    </h2>
                    <p className="text-sm text-content-muted mt-1">Clé API OpenRouteService (gratuite, sans carte bancaire), utilisée pour calculer automatiquement les frais kilométriques des clients Prestation qui interviennent à domicile.</p>
                </div>
                <div className="p-6 space-y-3">
                    <label className="text-xs font-bold text-content-muted uppercase tracking-widest">Clé API OpenRouteService</label>
                    <div className="flex items-center gap-3 max-w-lg">
                        <input type="password" value={openRouteServiceApiKey} onChange={(e) => { setOpenRouteServiceApiKey(e.target.value); setTestResult(null); }}
                            placeholder="eyJvcmciOi..."
                            className="flex-1 bg-surface-soft border border-border rounded-xl px-4 py-3 text-sm text-content outline-none focus:border-brand-500 transition-colors font-mono" />
                        <button type="button" onClick={handleTestKey} disabled={!openRouteServiceApiKey.trim() || testingKey}
                            className="shrink-0 flex items-center gap-2 px-4 py-3 rounded-xl border border-border text-content-muted hover:text-content hover:bg-surface-soft text-sm font-bold transition-all disabled:opacity-50">
                            <FlaskConical size={15} /> {testingKey ? 'Test...' : 'Tester la clé'}
                        </button>
                    </div>
                    {testResult && (
                        <p className={`text-xs font-semibold ${testResult.success ? 'text-emerald-500' : 'text-red-500'}`}>{testResult.text}</p>
                    )}
                    <p className="text-xs text-content-muted">Une seule clé pour toute la plateforme — les établissements n'ont qu'à activer l'option et fixer leur prix au km, sans rien configurer eux-mêmes. Créez une clé gratuite (2000 requêtes/jour) sur openrouteservice.org, aucune carte bancaire requise. Le test fonctionne même avant d'avoir enregistré la clé.</p>
                </div>
            </div>

            <div className="flex justify-end">
                <button
                    onClick={saveSettings}
                    disabled={saving}
                    className="px-6 py-3 bg-brand-600 hover:bg-brand-700 text-brand-fg font-bold rounded-xl shadow-lg shadow-brand-500/20 transition-all flex items-center gap-2 disabled:opacity-50"
                >
                    <Save size={18} />
                    {saving ? 'Sauvegarde...' : 'Sauvegarder les paramètres'}
                </button>
            </div>
            </>
            )}
        </div>
    );
};

export default AdminSettings;
